/** Stream-host classification for Connector proxy URLs (loopback / LAN / tunnel). */

import { diagnosticLog } from '../utils/diagnosticLogger';
import { getTauriDesktopOs, isWindowsConnectorHost } from '../utils/platform';

export type StreamHostKind = 'loopback' | 'lan_ip' | 'tunnel' | 'other';

/**
 * True when the resolved stream URL is served by the home Connector proxy on
 * loopback or LAN — safe for the native exclusive engine. Tunnel / remote URLs
 * (trycloudflare, https) must stay on WebAudio/HTML5.
 */
export function isLocalStreamUrl(url: string): boolean {
  const kind = classifyStreamHostKind(url);
  return kind === 'loopback' || kind === 'lan_ip';
}

/** Classify stream URL host for diagnostics (mixed-content vs reachability). */
export function classifyStreamHostKind(url: string): StreamHostKind {
  try {
    const u = new URL(url);
    if (u.protocol === 'https:' || /trycloudflare\.com/i.test(u.hostname)) {
      return 'tunnel';
    }
    if (u.hostname === '127.0.0.1' || u.hostname === 'localhost') {
      return 'loopback';
    }
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(u.hostname)) {
      return 'lan_ip';
    }
    return 'other';
  } catch {
    return 'other';
  }
}

/**
 * @deprecated Connector returns the correct host per consumer (loopback vs LAN).
 * Kept as identity passthrough for callers that still import it.
 */
export function rewriteLocalRendererStreamUrl(url: string): string {
  return url;
}

const LOCAL_STREAM_HOSTKIND_MSG = 'local stream hostKind';

function isLocalRendererStreamUrl(url: string): boolean {
  try {
    return new URL(url).pathname.includes('/stream/');
  } catch {
    return url.includes('/stream/');
  }
}

/**
 * Detect and repair double-wrapped /stream/ URLs produced by a contaminated
 * tunnel base URL.  A valid stream URL has exactly ONE /stream/{session}/{hmac}/{exp}
 * segment.  If a second /stream/ segment is present, strip everything before the
 * last /stream/ so only the freshest session survives.
 */
export function sanitizeStreamUrl(url: string): string {
  try {
    const u = new URL(url);
    const last = u.pathname.lastIndexOf('/stream/');
    if (last > 0) {
      u.pathname = u.pathname.slice(last);
      const repaired = u.href;
      if (repaired !== url) {
        console.warn('[PlaybackBridge] Repaired double-wrapped URL');
      }
      return repaired;
    }
    return url;
  } catch {
    const marker = '/stream/';
    const last = url.lastIndexOf(marker);
    if (last <= 0) return url;
    const first = url.indexOf('://');
    const hostEnd = url.indexOf('/', first >= 0 ? first + 3 : 0);
    if (hostEnd < 0 || last <= hostEnd) return url;
    console.warn('[PlaybackBridge] Repaired double-wrapped URL');
    return url.slice(0, hostEnd) + url.slice(last);
  }
}

/** Fleet morning report: emit hostKind whenever a local-renderer stream URL is finalized. */
export function emitLocalStreamHostKindDiagnostic(
  urlBefore: string,
  urlAfter: string,
  opts: { outcome?: 'success' | 'failure'; context?: string; error?: string } = {},
): void {
  const before = String(urlBefore || '').trim();
  const after = String(urlAfter || '').trim();
  const probe = after || before;
  if (!probe || !isLocalRendererStreamUrl(probe)) return;

  const hostKindAfter = classifyStreamHostKind(after || before);
  const hostKindBefore = before && before !== after ? classifyStreamHostKind(before) : undefined;

  diagnosticLog('info', 'js:playback', LOCAL_STREAM_HOSTKIND_MSG, {
    hostKind: hostKindAfter,
    hostKindBefore,
    rewritten: before !== after,
    outcome: opts.outcome ?? 'success',
    context: opts.context,
    error: opts.error,
    windowsHost: isWindowsConnectorHost(),
    detectedOs: getTauriDesktopOs() ?? undefined,
    pageOriginHttps: typeof window !== 'undefined' && window.location?.protocol === 'https:',
  });
}

export function finalizeLocalPlaybackUrl(
  url: string,
  diag?: { outcome?: 'success' | 'failure'; context?: string; error?: string },
): string {
  const sanitized = sanitizeStreamUrl(url);
  emitLocalStreamHostKindDiagnostic(sanitized, sanitized, {
    outcome: diag?.outcome ?? 'success',
    context: diag?.context ?? 'finalizeLocalPlaybackUrl',
    error: diag?.error,
  });
  return sanitized;
}

/** Sanitize stream URL + fleet hostKind diagnostic (Connector owns host selection). */
export function resolveLocalRendererStreamUrl(
  url: string,
  diag?: { outcome?: 'success' | 'failure'; context?: string; error?: string },
): string {
  return finalizeLocalPlaybackUrl(url, diag);
}
